// A refactored version of our SqFetch but doesn't access the global window object.

/**
 * Retrieves the CSRF token from the meta tag.
 * @returns {string | null} - The CSRF token or null if not found.
 * @private
 */
const _getCsrfToken = (): string | null => {
  const csrfTokenMeta = document.querySelector('meta[name=csrf-token]');
  return csrfTokenMeta ? csrfTokenMeta.getAttribute('content') : null;
};

/**
 * A wrapper function for the fetch API that includes default options and allows for overrides.
 *
 * @param {string} url - The URL to which the request is sent.
 * @param {string} method - The HTTP method to use for the request (e.g., 'GET', 'POST').
 * @param {any} requestBody - The body of the request, which will be stringified if the content type is 'application/json'.
 * @param {RequestInit} overrides - An object containing any additional options to override the default settings.
 * @returns {Promise<Response>} - A promise that resolves to the response of the fetch request.
 */
const _fetch = (
  url: string,
  method: string,
  requestBody: any,
  overrides: RequestInit,
): Promise<Response> => {
  const options: RequestInit = {
    method,
    credentials: 'include',
    headers: {
      'Content-Type': overrides.headers?.['Content-Type'] || 'application/json',
      'X-CSRF-Token': _getCsrfToken(),
    },
    ...overrides,
  };

  if (requestBody) {
    options.body =
      options.headers['Content-Type'] === 'application/json'
        ? JSON.stringify(requestBody)
        : requestBody;
  }

  return fetch(url, options);
};

/**
 * A module that provides a set of functions for making HTTP requests.
 * @module SqFetch
 * @example import SqFetch from 'typescript/application/sq_fetch';
 * @example SqFetch.get('/test-url').then(response => console.log(response));
 */
export default {
  get: (url: string, options: RequestInit = {}): Promise<Response> => {
    return _fetch(url, 'GET', null, options);
  },
  put: (
    url: string,
    request: any,
    options: RequestInit = {},
  ): Promise<Response> => {
    return _fetch(url, 'PUT', request, options);
  },
  post: (
    url: string,
    request: any,
    options: RequestInit = {},
  ): Promise<Response> => {
    return _fetch(url, 'POST', request, options);
  },
  delete: (url: string, options: RequestInit = {}): Promise<Response> => {
    return _fetch(url, 'DELETE', null, options);
  },
};
